import { useState, type ReactNode } from 'react';
import type { Creator } from '@/types';
import {
  useAcceptAppeal,
  useRejectAppeal,
} from '../hooks/use-ai-admin-actions';
import { AiAppealRejectForm } from './ai-appeal-reject-form';
import { AiAppealAcceptForm } from './ai-appeal-accept-form';
import {
  AppealRowHeader,
  CurrentCategoryLine,
  DecisionButtons,
  ReasoningPair,
} from './ai-appeal-row-parts';

type Mode = 'view' | 'reject' | 'accept';

const ROW =
  'flex flex-col gap-sm rounded-lg border border-white/[0.06] bg-surface/40 p-md';

type RejectMut = ReturnType<typeof useRejectAppeal>;
type AcceptMut = ReturnType<typeof useAcceptAppeal>;

function RejectMode(p: {
  creator: Creator;
  reject: RejectMut;
  back: () => void;
}): ReactNode {
  return (
    <AiAppealRejectForm
      isPending={p.reject.isPending}
      onCancel={p.back}
      onConfirm={(adminNote) =>
        p.reject.mutate({ id: p.creator.id, adminNote })
      }
    />
  );
}

function AcceptMode(p: {
  creator: Creator;
  accept: AcceptMut;
  back: () => void;
}): ReactNode {
  return (
    <AiAppealAcceptForm
      creator={p.creator}
      isPending={p.accept.isPending}
      onCancel={p.back}
      onConfirm={(extraData) =>
        p.accept.mutate({ id: p.creator.id, extraData })
      }
    />
  );
}

function Actions({ creator }: { creator: Creator }): ReactNode {
  const [mode, setMode] = useState<Mode>('view');
  const accept = useAcceptAppeal();
  const reject = useRejectAppeal();
  const back = (): void => setMode('view');
  if (mode === 'reject')
    return <RejectMode creator={creator} reject={reject} back={back} />;
  if (mode === 'accept')
    return <AcceptMode creator={creator} accept={accept} back={back} />;
  return (
    <DecisionButtons
      onAccept={() => setMode('accept')}
      onStartReject={() => setMode('reject')}
      pending={accept.isPending || reject.isPending}
    />
  );
}

export function AiAppealRow({ creator }: { creator: Creator }): ReactNode {
  return (
    <div className={ROW}>
      <AppealRowHeader creator={creator} />
      <CurrentCategoryLine creator={creator} />
      <ReasoningPair creator={creator} />
      <Actions creator={creator} />
    </div>
  );
}
